/**
 * =============================================================================
 * AppShellSidebar — Navegação 2.0 (rail de categorias + painel de submenus)
 * Versão: 1.1.0 (2026-08-05)
 *
 * Descrição:
 *   Menu lateral único de dois níveis, no estilo Pipedrive:
 *     - Nível 1 (rail): categorias com ícone + rótulo curto.
 *     - Nível 2 (painel): submenus da categoria selecionada.
 *
 *   Mudança EXCLUSIVAMENTE de UX/UI: rotas, permissões, guards e flags de
 *   módulo continuam exatamente iguais (ver useNavTree / navRegistry).
 *
 * CHANGELOG:
 *   v1.10.0 (2026-09-07)
 *     - Personalização do rail passa a ser persistida na CONTA do usuário
 *       (RPC set_user_preferences, chave `sidebar_menus`), via o novo hook
 *       `useSidebarMenusPrefs`. localStorage (`navSidebarMenus`) fica apenas
 *       como cache de primeira pintura; ao carregar, a preferência do banco
 *       é a fonte da verdade. Saneamento por permissão mantido.
 *   v1.9.0 (2026-09-07)
 *     - Nova opção "Personalizar menu" no popover do botão "Mais": modal para
 *       escolher quais menus pais aparecem no rail.
 *     - Dashboard, Chat, CRM e Kanban viram fixos (não desmarcáveis) e o
 *       usuário escolhe até 4 menus adicionais (persistidos em
 *       `navSidebarMenus`). Rail = 4 fixos + 4 escolhidos + slot extra + "Mais".
 *   v1.8.0 (2026-09-07)
 *     - Rail passa a exibir no máximo 8 menus pais fixos; os demais ficam no
 *       novo botão "Mais" (popover com ícone + nome).
 *     - Um único "slot extra" acima do botão "Mais" hospeda o menu escolhido
 *       enquanto a rota atual pertencer a ele (persistido em
 *       `navExtraCategory`, sobrevive a recarregamentos).
 *   v1.7.0 (2026-08-25)
 *     - Destaque do rail passa a respeitar a categoria fixada pelo clique
 *       (ex.: "Kanban") em vez da categoria derivada da URL (ex.: "CRM").
 *     - Fixação persistida em localStorage (`navPinnedCategory`), sobrevivendo
 *       a recarregamentos da página; liberada quando a rota deixa a categoria.
 *   v1.6.0 (2026-08-25)
 *     - Menu pai deixa de trocar sozinho ao clicar em submenus: a categoria de
 *       origem é "fixada" no clique e só é liberada quando a rota atual não
 *       pertence mais a ela (ou o usuário escolhe outra categoria no rail).
 *   v1.5.0 (2026-08-06)
 *     - Rail com layout ÚNICO (ícone + nome) também no estado retraído:
 *       `--sidebar-width-icon` igual à largura do rail (6.75rem), rótulos
 *       sempre visíveis e remoção dos tooltips do modo só-ícone.
 *     - Removida a migração `navRailLayoutV2` (não é mais necessária).

 *   v1.4.0 (2026-08-06)
 *     - Novo layout do rail: ícone + nome do menu como formato PADRÃO, com
 *       rail mais largo (6.75rem), ícone maior e rótulo de 11px.
 *     - Migração única de preferência (`navRailLayoutV2`): limpa o cookie
 *       antigo que deixava usuários presos no modo só-ícone.
 *   v1.3.0 (2026-08-06)
 *     - Restaurado o controle manual de retrair/expandir (ícone sanduíche do
 *       header): removido o forçar "expanded" na montagem; o estado do usuário
 *       volta a ser respeitado e persistido pelo SidebarProvider.
 *     - Modo retraído: rail só com ícones centralizados + tooltip; painel de
 *       submenus não é renderizado.
 *   v1.2.0 (2026-08-06)
 *     - Rail sempre expandido no desktop (ícone + rótulo): força o estado
 *       "expanded" na montagem, ignorando cookie antigo de "collapsed".
 *     - Removido o botão de retrair (ChevronLeft) do cabeçalho.
 *   v1.1.0 (2026-08-05)
 *     - Painel não abre mais vazio: categorias sem submenu visível (e sem
 *       workspaces na seção especial do CRM) navegam direto.
 *     - Cabeçalho usa a logo completa (branding.logo_url) quando expandido.
 *     - Removido o rodapé de usuário/sair (ação segue no avatar do header).
 *   v1.0.0 (2026-08-05) - Versão inicial.
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Settings2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useNavTree } from '@/components/navigation/useNavTree';
import type { NavCategory } from '@/components/navigation/navRegistry';
import { AppNavPanel } from '@/components/navigation/AppNavPanel';
import { CustomizeSidebarMenuDialog } from '@/components/navigation/CustomizeSidebarMenuDialog';
import { useSidebarMenusPrefs } from '@/hooks/useSidebarMenusPrefs';

/** Chave de persistência do painel (escopo por usuário). */
const PANEL_KEY = 'navPanelCategory';

/** Chave de persistência da categoria "fixada" pelo clique do usuário. */
const PINNED_KEY = 'navPinnedCategory';

/** Chave de persistência do menu pai promovido ao "slot extra" (menu Mais). */
const EXTRA_KEY = 'navExtraCategory';

/** Chave de persistência dos menus opcionais escolhidos para o rail. */
const MENUS_KEY = 'navSidebarMenus';

/** Menus pais sempre fixos no rail (não desmarcáveis). */
const FIXOS_IDS = ['dashboard', 'chat', 'crm', 'kanban'];

/** Quantidade máxima de menus opcionais escolhidos pelo usuário. */
const MAX_OPCIONAIS = 4;

/** Lê a lista de menus opcionais escolhidos (array de ids). */
function readMenus(): string[] | null {
  try {
    const raw = localStorage.getItem(MENUS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : null;
  } catch {
    /* localStorage/JSON indisponível — ignora silenciosamente */
    return null;
  }
}

/** Grava a lista de menus opcionais escolhidos. */
function writeMenus(ids: string[]) {
  try {
    localStorage.setItem(MENUS_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage indisponível — ignora silenciosamente */
  }
}

/** Lê uma categoria persistida ({ categoryId, path }) da chave informada. */
function readStored(key: string): { categoryId: string; path: string } | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.categoryId && typeof parsed.categoryId === 'string') {
      return { categoryId: parsed.categoryId, path: String(parsed.path ?? '') };
    }
    return null;
  } catch {
    /* localStorage/JSON indisponível — ignora silenciosamente */
    return null;
  }
}

/** Grava (ou limpa) uma categoria persistida junto com a rota que a originou. */
function writeStored(key: string, categoryId: string | null, path: string) {
  try {
    if (!categoryId) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify({ categoryId, path }));
  } catch {
    /* localStorage indisponível — ignora silenciosamente */
  }
}

/** Atalhos para a categoria fixada pelo clique do usuário. */
const readPinned = () => readStored(PINNED_KEY);
const writePinned = (categoryId: string | null, path: string) =>
  writeStored(PINNED_KEY, categoryId, path);

export function AppShellSidebar() {
  // `state` reflete a escolha do usuário (sanduíche do header), persistida em
  // cookie pelo SidebarProvider. Nada é forçado aqui.
  // Retraído afeta APENAS o painel de submenus — o rail mantém ícone + nome.
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;







  const { t } = useTranslation('sidebar');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { branding } = useSystemSettings();
  const { switchModule } = useHeaderModuleFlags();
  const { categories, activeCategoryId, loading } = useNavTree();

  // Workspaces do CRM: usados apenas para saber se a seção especial tem
  // conteúdo (evita abrir o painel em branco).
  const { workspaces } = useCrmWorkspacesHierarchy();
  const hasWorkspaces = (workspaces?.length ?? 0) > 0;

  /** Uma categoria só abre painel quando existe conteúdo para exibir. */
  const hasPanelContent = (cat?: NavCategory | null) => {
    if (!cat) return false;
    const childrenCount = cat.children?.length ?? 0;
    if (cat.specialSection === 'crmWorkspaces') {
      return childrenCount > 1 || (childrenCount >= 1 && hasWorkspaces) || hasWorkspaces;
    }
    return childrenCount > 0;
  };

  // Categoria aberta no painel (nível 2).
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);

  // Categoria "fixada" pelo usuário: seja por clique no rail, seja por clique
  // em um submenu dentro do painel. Enquanto a rota atual ainda pertencer a
  // essa categoria, o painel NÃO troca de menu pai e o rail mantém o destaque
  // nessa categoria (mesmo que a URL pertença a outro módulo).
  // O ref garante leitura síncrona dentro do efeito; o state alimenta o render.
  const userOpenedRef = useRef<string | null>(readPinned()?.categoryId ?? null);
  const [pinnedCategoryId, setPinnedCategoryId] = useState<string | null>(
    () => readPinned()?.categoryId ?? null,
  );

  // Menu pai escolhido dentro do botão "Mais": ocupa o único "slot extra" do
  // rail enquanto a rota atual pertencer a ele (persistido para sobreviver a
  // recarregamentos da página).
  const [slotExtraId, setSlotExtraId] = useState<string | null>(
    () => readStored(EXTRA_KEY)?.categoryId ?? null,
  );

  /** Promove (ou libera) o menu pai do slot extra, persistindo a rota atual. */
  const applySlotExtra = (categoryId: string | null, path: string) => {
    setSlotExtraId(categoryId);
    writeStored(EXTRA_KEY, categoryId, path);
  };

  /** Controle de abertura do popover do botão "Mais". */
  const [maisAberto, setMaisAberto] = useState(false);

  /**
   * Menus opcionais escolhidos pelo usuário.
   * Estado inicial vem do cache local (primeira pintura); quando a
   * preferência da conta chega (via useSidebarMenusPrefs), ela assume.
   */
  const [menusSalvos, setMenusSalvos] = useState<string[] | null>(() => readMenus());

  /** Preferência persistida na conta (banco), com gravação otimista. */
  const { menusServidor, salvar: salvarMenusServidor } = useSidebarMenusPrefs();

  // Reconciliação: a conta é a fonte da verdade. Lista vazia = nunca
  // personalizado (mantém o cache local/padrão para não apagar escolhas
  // feitas antes desta persistência existir).
  useEffect(() => {
    if (!menusServidor || menusServidor.length === 0) return;
    setMenusSalvos((atual) =>
      atual && atual.length === menusServidor.length &&
      atual.every((id, i) => id === menusServidor[i])
        ? atual
        : menusServidor,
    );
    writeMenus(menusServidor);
  }, [menusServidor]);

  /** Controle de abertura do modal "Personalizar menu". */
  const [personalizarAberto, setPersonalizarAberto] = useState(false);

  /** Fixa (ou libera) a categoria de origem, persistindo com a rota atual. */
  const applyPinned = (categoryId: string | null, path: string) => {
    userOpenedRef.current = categoryId;
    setPinnedCategoryId(categoryId);
    writePinned(categoryId, path);
  };


  /** Coleta recursivamente as URLs (sem querystring) de itens do painel. */
  const collectUrls = (items?: NavCategory['children']): string[] =>
    (items ?? []).flatMap((item) => [
      ...(item.url ? [item.url.split('?')[0]] : []),
      ...collectUrls(item.children),
    ]);

  /** A rota atual pertence a esta categoria (prefixos próprios ou filhos)? */
  const categoryMatchesPath = (cat: NavCategory | undefined, path: string) => {
    if (!cat) return false;
    const prefixes = [
      ...(cat.matchPrefixes ?? []),
      ...(cat.url ? [cat.url.split('?')[0]] : []),
      ...collectUrls(cat.children),
    ];
    return prefixes.some((p) => path === p || path.startsWith(`${p}/`));
  };

  // Sincroniza o painel com a rota atual (abre na categoria ativa).
  useEffect(() => {
    // Categoria fixada pelo usuário: mantém o painel enquanto a rota atual
    // ainda fizer parte dela (ex.: "Kanban > Propostas" abre uma rota de CRM,
    // mas o menu pai deve continuar em "Kanban"). Caso contrário, libera o pin.
    const pinnedId = userOpenedRef.current;
    if (pinnedId) {
      const pinned = categories.find((c) => c.id === pinnedId);
      if (pinned && categoryMatchesPath(pinned, pathname)) {
        if (pinnedCategoryId !== pinnedId) setPinnedCategoryId(pinnedId);
        writePinned(pinnedId, pathname);
        if (hasPanelContent(pinned)) setOpenCategoryId(pinnedId);
        return;
      }
      // Navegação fora do painel fixado: volta ao comportamento automático.
      // (não limpa antes das categorias carregarem, para não perder o pin)
      if (categories.length > 0) applyPinned(null, pathname);
    }


    if (!activeCategoryId) return;

    const cat = categories.find((c) => c.id === activeCategoryId);
    if (!hasPanelContent(cat)) {
      // Rota de categoria sem submenus: mantém o painel fechado.
      setOpenCategoryId(null);
      return;
    }
    setOpenCategoryId(activeCategoryId);
    try {
      localStorage.setItem(PANEL_KEY, activeCategoryId);
    } catch {
      /* localStorage indisponível — ignora silenciosamente */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryId, categories, hasWorkspaces, pathname]);

  const openCategory = categories.find(
    (c) => c.id === openCategoryId && hasPanelContent(c),
  );

  // ---------------------------------------------------------------- Menu "Mais"
  // Rail = menus fixos (Dashboard, Chat, CRM, Kanban) + até 4 menus opcionais
  // escolhidos pelo usuário. Todos os demais ficam no botão "Mais".
  const menusEscolhidos = useMemo(() => {
    const disponiveis = categories
      .filter((c) => !FIXOS_IDS.includes(c.id))
      .map((c) => c.id);
    const salvos = menusSalvos?.filter((id) => disponiveis.includes(id));
    // Sem preferência salva (ou toda inválida): padrão = 4 primeiros não fixos.
    const efetivos = salvos && salvos.length > 0 ? salvos : disponiveis;
    return efetivos.slice(0, MAX_OPCIONAIS);
  }, [categories, menusSalvos]);

  const categoriasFixas = [
    ...FIXOS_IDS.map((id) => categories.find((c) => c.id === id)).filter(
      (c): c is NavCategory => !!c,
    ),
    ...menusEscolhidos
      .map((id) => categories.find((c) => c.id === id))
      .filter((c): c is NavCategory => !!c),
  ];
  const fixasIds = new Set(categoriasFixas.map((c) => c.id));
  const categoriasExtras = categories.filter((c) => !fixasIds.has(c.id));

  // Categoria em destaque (fixada pelo clique ou derivada da rota).
  const categoriaDestacadaId = pinnedCategoryId ?? activeCategoryId;

  // Sincroniza o slot extra com a rota/seleção atual: ele só permanece
  // enquanto a tela pertencer ao menu escolhido (ou enquanto ele estiver
  // aberto/destacado). Também é preenchido automaticamente quando a categoria
  // em destaque está fora dos 8 fixos (ex.: acesso por link direto).
  useEffect(() => {
    if (categories.length === 0) return;

    const extraIds = new Set(categoriasExtras.map((c) => c.id));

    // Prioridade: categoria destacada/aberta que não cabe entre as fixas.
    const candidato =
      (categoriaDestacadaId && extraIds.has(categoriaDestacadaId)
        ? categoriaDestacadaId
        : null) ??
      (openCategoryId && extraIds.has(openCategoryId) ? openCategoryId : null);

    if (candidato) {
      if (candidato !== slotExtraId) applySlotExtra(candidato, pathname);
      else writeStored(EXTRA_KEY, candidato, pathname);
      return;
    }

    // Sem candidato: mantém o slot apenas se a rota ainda pertencer a ele.
    if (slotExtraId) {
      const cat = categories.find((c) => c.id === slotExtraId);
      if (!cat || !extraIds.has(slotExtraId) || !categoryMatchesPath(cat, pathname)) {
        applySlotExtra(null, pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, pathname, categoriaDestacadaId, openCategoryId, slotExtraId]);

  const categoriaSlotExtra = categoriasExtras.find((c) => c.id === slotExtraId);

  /** Renderiza um botão de menu pai do rail. */
  const renderRailButton = (cat: NavCategory) => {
    const Icon = cat.icon;
    const isActive = categoriaDestacadaId === cat.id;
    const isOpen = openCategoryId === cat.id && !collapsed;
    const label = t(cat.titleKey, cat.defaultLabel);

    return (
      <button
        key={cat.id}
        type="button"
        onClick={() => handleCategoryClick(cat)}
        className={cn(
          'flex w-full flex-col items-center gap-1.5 rounded-lg px-1.5 py-2.5 text-[11px] font-medium leading-tight transition-colors',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive || isOpen
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/80',
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        <Icon className="h-[1.35rem] w-[1.35rem] shrink-0" />
        <span className="line-clamp-2 text-center">{label}</span>
      </button>
    );
  };


  /** Clique numa categoria do rail. */
  const handleCategoryClick = (cat: NavCategory) => {
    if (hasPanelContent(cat) && !collapsed) {
      // Categoria pai: apenas abre/fecha o painel, SEM navegar e SEM
      // switchModule (switchModule navega para a última rota do módulo e
      // fechava o painel recém-aberto — causa do "só abre no 2º clique").
      setOpenCategoryId((prev) => {
        const next = prev === cat.id ? null : cat.id;
        applyPinned(next, pathname);
        return next;
      });

      return;
    }

    // Troca de módulo preserva "última rota por módulo" (useActiveModule).
    if (cat.moduleType) switchModule(cat.moduleType);

    // Sem conteúdo de painel (ou rail retraído): navega direto
    // (link próprio ou primeiro filho).
    applyPinned(null, pathname);
    setOpenCategoryId(null);
    const target = cat.url ?? cat.children?.[0]?.url;
    if (target && !(cat.url && cat.moduleType)) navigate(target);
    if (isMobile) setOpenMobile(false);
  };


  // No modo retraído o painel de submenus não é exibido.
  const showPanel = !!openCategory && !collapsed;

  // Logo completa quando o menu está largo (painel aberto) OU no mobile
  // (o menu abre como Sheet ocupando quase toda a tela).
  const useFullLogo = (showPanel || isMobile) && !!branding.logo_url;

  return (
    <Sidebar
      collapsible="icon"
      style={
        {
          '--sidebar-width': showPanel ? '21.25rem' : '6.75rem',
          // Retraído mantém a mesma largura do rail (ícone + nome).
          '--sidebar-width-icon': '6.75rem',

        } as React.CSSProperties
      }
    >
      {/* Branding */}
      <SidebarHeader className="border-b border-sidebar-border/50">
        <div className="flex items-center justify-center px-2 py-3">
          <div className="flex min-w-0 items-center justify-center">
            {useFullLogo ? (
              <img
                src={branding.logo_url as string}
                alt={branding.app_name}
                className="h-9 w-auto max-w-full object-contain"
              />
            ) : branding.icon_url ? (
              <img
                src={branding.icon_url}
                alt={branding.app_name}
                className="h-9 w-9 shrink-0 rounded-lg object-contain"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
                {branding.app_name[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="overflow-hidden p-0">
        <div className="flex h-full min-h-0 w-full">
          {/* ------------------------------- Nível 1: rail de categorias */}
          
          <nav className="flex h-full min-h-0 w-[6.75rem] shrink-0 flex-col gap-1 overflow-y-auto overflow-x-hidden px-2 py-2">
            {loading ? (
              [1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))
            ) : (
              <>
                {/* Menus pais fixos (máx. 8) */}
                {categoriasFixas.map((cat) => renderRailButton(cat))}

                {/* Slot extra: menu pai escolhido no botão "Mais" */}
                {categoriaSlotExtra && renderRailButton(categoriaSlotExtra)}

                {/* Botão "Mais": menus pais restantes + personalizar menu */}
                {(
                  <Popover open={maisAberto} onOpenChange={setMaisAberto}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full flex-col items-center gap-1.5 rounded-lg px-1.5 py-2.5 text-[11px] font-medium leading-tight transition-colors',
                          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                          maisAberto
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'text-sidebar-foreground/80',
                        )}
                        aria-label="Mais menus"
                      >
                        <MoreHorizontal className="h-[1.35rem] w-[1.35rem] shrink-0" />
                        <span className="line-clamp-2 text-center">Mais</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="right"
                      align="end"
                      sideOffset={8}
                      className="w-64 p-1.5"
                    >
                      <div className="flex flex-col">
                        {categoriasExtras.map((cat) => {
                          const Icon = cat.icon;
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => {
                                // Promove ao slot extra e reaproveita o mesmo
                                // comportamento de clique do rail.
                                applySlotExtra(cat.id, pathname);
                                setMaisAberto(false);
                                handleCategoryClick(cat);
                              }}
                              className={cn(
                                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                                'hover:bg-accent hover:text-accent-foreground',
                                categoriaDestacadaId === cat.id
                                  ? 'bg-accent text-accent-foreground'
                                  : 'text-foreground',
                              )}
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              <span className="truncate">
                                {t(cat.titleKey, cat.defaultLabel)}
                              </span>
                            </button>
                          );
                        })}

                        {/* Separador + acesso à personalização do rail */}
                        {categoriasExtras.length > 0 && (
                          <div className="my-1.5 h-px bg-border" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setMaisAberto(false);
                            setPersonalizarAberto(true);
                          }}
                          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <Settings2 className="h-4 w-4 shrink-0" />
                          <span className="truncate">Personalizar menu</span>
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </>
            )}
          </nav>






          {/* ------------------------------- Nível 2: painel de submenus */}
          {showPanel && openCategory && (
            <AppNavPanel
              category={openCategory}
              onNavigate={(url) => {
                // Fixa a categoria de origem do clique para que o menu pai não
                // troque quando a rota destino pertencer a outra categoria.
                applyPinned(openCategory.id, (url ?? pathname).split('?')[0]);
                if (isMobile) setOpenMobile(false);

              }}
            />
          )}
        </div>
      </SidebarContent>

      {/* Modal de personalização da barra de navegação */}
      <CustomizeSidebarMenuDialog
        open={personalizarAberto}
        onOpenChange={setPersonalizarAberto}
        categories={categories}
        fixedIds={FIXOS_IDS}
        selectedIds={menusEscolhidos}
        maxOpcionais={MAX_OPCIONAIS}
        onSave={(ids) => {
          // Persiste na conta (banco) e espelha no cache local imediatamente
          salvarMenusServidor(ids);
          setMenusSalvos(ids);
          writeMenus(ids);
        }}
      />
    </Sidebar>
  );
}
